import { useState, useEffect, useMemo } from "react";
import { getLocale } from "../../handler/LocaleHandler";

/**
 * Extracts localized text from widget configuration
 * Supports only object format: {"en": "text", "ja": "text"}
 *
 * @param {Object} localizationData - Localization data in object format
 * @param {string} currentLocale - Current locale (e.g., 'en', 'ja', 'es')
 * @returns {string} Localized text with fallback logic
 */
export function getLocalizedText(localizationData, currentLocale) {
  // Handle null/undefined/empty values
  if (!localizationData) {
    return "Invalid Format";
  }

  // Use 'en' as fallback if currentLocale is invalid/empty
  if (
    !currentLocale ||
    currentLocale === "" ||
    typeof currentLocale !== "string"
  ) {
    currentLocale = "en";
  }

  // Handle object format: {"en": "text", "ja": "text"}
  if (
    typeof localizationData === "object" &&
    !Array.isArray(localizationData)
  ) {
    // Try to find exact locale match
    if (
      localizationData[currentLocale] &&
      typeof localizationData[currentLocale] === "string"
    ) {
      const text = localizationData[currentLocale].trim();
      if (text !== "") {
        return text;
      }
    }

    // Fallback to English
    if (localizationData.en && typeof localizationData.en === "string") {
      const text = localizationData.en.trim();
      if (text !== "") {
        return text;
      }
    }
  }

  return "";
}

/**
 * Helper function to detect if a field is localizable
 * @param {*} value - Field value to check
 * @returns {boolean} True if field contains localizable object format
 */
function isLocalizableField(value) {
  return (
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    Object.keys(value).some((key) => typeof value[key] === "string")
  );
}

/**
 * React hook for widget localization - Generic version
 * Automatically detects and localizes all localizable fields
 * Handles async locale detection and provides localized widget fields
 *
 * @param {Object} widgetDefinition - Widget definition object containing any localizable fields
 * @returns {Object} Object containing all localized fields and loading state
 */
export function useWidgetLocalization(widgetDefinition) {
  const [currentLocale, setCurrentLocale] = useState("en"); // Default to 'en' for initial render
  const [isLocaleLoading, setIsLocaleLoading] = useState(true);

  // Fetch locale on mount
  useEffect(() => {
    let isMounted = true;

    const fetchLocale = async () => {
      try {
        const locale = await getLocale();

        if (isMounted) {
          setCurrentLocale(locale || "en");
          setIsLocaleLoading(false);
        }
      } catch (error) {
        if (isMounted) {
          setCurrentLocale("en");
          setIsLocaleLoading(false);
        }
      }
    };

    fetchLocale();

    return () => {
      isMounted = false;
    };
  }, []);

  // Generic localization - processes ANY field that looks localizable
  const localizedFields = useMemo(() => {
    if (!widgetDefinition || typeof widgetDefinition !== "object") {
      return {};
    }

    const localized = {};

    // Process each field in the widget definition
    Object.keys(widgetDefinition).forEach((fieldName) => {
      const fieldValue = widgetDefinition[fieldName];

      // Check if field is localizable (object format)
      if (isLocalizableField(fieldValue)) {
        localized[fieldName] = getLocalizedText(fieldValue, currentLocale);
      }
      // Keep non-localizable fields as-is
      else {
        localized[fieldName] = fieldValue;
      }
    });

    return localized;
  }, [widgetDefinition, currentLocale]);

  return {
    ...localizedFields,
    currentLocale,
    isLocaleLoading,
  };
}

/**
 * Utility function to localize a single widget definition object - Generic version
 * Automatically detects and localizes all localizable fields
 * Useful for transforming widget configs before passing to components
 *
 * @param {Object} widgetDefinition - Widget definition object containing any localizable fields
 * @param {string} currentLocale - Current locale
 * @returns {Object} Widget definition with localized string fields
 */
export function localizeWidgetDefinition(widgetDefinition, currentLocale) {
  if (!widgetDefinition || typeof widgetDefinition !== "object") {
    return widgetDefinition || {};
  }

  if (!currentLocale || typeof currentLocale !== "string") {
    currentLocale = "en";
  }

  // Create localized copy
  const localizedDefinition = {};

  // Process each field in the widget definition
  Object.keys(widgetDefinition).forEach((fieldName) => {
    const fieldValue = widgetDefinition[fieldName];

    // Check if field is localizable (object format)
    if (isLocalizableField(fieldValue)) {
      localizedDefinition[fieldName] = getLocalizedText(
        fieldValue,
        currentLocale,
      );
    }
    // Keep non-localizable fields as-is
    else {
      localizedDefinition[fieldName] = fieldValue;
    }
  });

  return localizedDefinition;
}



// WEBPACK FOOTER //
// ./src/components/mySCC/util/WidgetLocalizationUtil.js